import { ChatUIContext } from '@/components/context/ChatUIContext';
import { CORPUS_QA, DATAVOYAGER, PAPER_FINDER } from 'configs/NoraVariants';
import {
  mkSendBtnTrackAttrs,
  mkTextBoxTrackAttrs,
} from '@/analytics/props/msgBar';
import { optAppConfig } from '@/utils/config';

import {
  Box,
  FormControl,
  IconButton,
  TextareaAutosize,
  Tooltip,
} from '@mui/material';
import {
  ChangeEvent,
  Key,
  KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ChipGroup, ChipItem, FileInputButton } from '@allenai/varnish-ui';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionIcon from '@mui/icons-material/Description';
import PendingIcon from '@mui/icons-material/Pending';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import styled from 'styled-components';

type MessageBarProps = {
  onSend: (text: string) => Promise<any>;
  onFilesChanged?: (files: File[]) => void;
  isPending?: boolean;
  placeholder?: string;
  focusOnLoad?: boolean;
};

const ENTER_KEY = 'Enter' as const;

const noraExperienceVariant = optAppConfig((_) => _.experience.variant);
const placeholderText =
  noraExperienceVariant === CORPUS_QA
    ? 'Enter a literature review question...'
    : noraExperienceVariant === PAPER_FINDER
    ? 'Search for papers...'
    : noraExperienceVariant === DATAVOYAGER
    ? 'Analyze some data...'
    : "Let's do some research";

const MessageBar = ({
  onSend,
  onFilesChanged = (files: File[]) => {
    console.debug('Files selected but no handler bound', files);
    return Promise.resolve();
  },
  isPending = false,
  placeholder = placeholderText,
  focusOnLoad,
}: MessageBarProps) => {
  const isFileUploadEnabled = optAppConfig((_) => _.file_upload.is_enabled);
  const formRef = useRef<HTMLFormElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const { inputValueAndSelection, setInputValueAndSelection } =
    useContext(ChatUIContext);
  const [isSelectionDirty, setIsSelectionDirty] = useState(false);
  const [attachments, setAttachments] = useState<
    { chip: ChipItem; file: File }[]
  >([]);

  const { text, selection } = inputValueAndSelection;
  const isEmpty = (text ?? '').trim().length === 0 && !attachments.length;

  useEffect(() => {
    // focus on message bar textarea on load if focusOnLoad is true
    if (textAreaRef.current && focusOnLoad) {
      textAreaRef.current.focus();
    }
  }, [focusOnLoad]);

  useEffect(() => {
    if (inputValueAndSelection.selection) {
      setIsSelectionDirty(true);
    }
  }, [selection]);

  useEffect(() => {
    if (!isSelectionDirty) {
      return;
    }
    const current = textAreaRef.current;
    if (selection && current) {
      current.focus();
      current.setSelectionRange(selection.start, selection.end);
    }
    setIsSelectionDirty(false);
  }, [isSelectionDirty]);

  const handleOnSend = useCallback(
    async (event: ChangeEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (isEmpty) {
        return;
      }
      await onSend(text);
      setInputValueAndSelection({ text: '' });
      if (isFileUploadEnabled && attachments.length > 0) {
        setAttachments([]);
      }
      const textAreaCurrent = textAreaRef.current;
      if (textAreaCurrent) {
        textAreaCurrent.blur();
      }
    },
    [onSend, text, isEmpty, setInputValueAndSelection, attachments],
  );

  const handleEnterKeyPress = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      // don't submit if nora is currently processing a message
      if (isPending) {
        return;
      }

      // prevent the default behavior (new line) when Enter is pressed to submit the form
      // if shift + enter is pressed, a new line should be created
      if (event.key === ENTER_KEY && !event.shiftKey && formRef.current) {
        event.preventDefault();
        formRef.current.requestSubmit();
      }
    },
    [formRef, isPending],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setInputValueAndSelection({ text: event.target.value });
    },
    [setInputValueAndSelection],
  );

  const onFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files) {
        return;
      }
      const newAttachments = Array.from(files).map((file, i) => {
        const chip = {
          id: i.toString(),
          text: file.name,
          icon: <DescriptionIcon />, // use different icon logic here once we accept non-text files
        };
        return { chip, file };
      });
      setAttachments((prev) => [...prev, ...newAttachments]);
    },
    [setAttachments],
  );

  const onChipRemove = useCallback(
    (keys: Set<Key>) => {
      setAttachments((prev) =>
        prev.filter((attachment) => !keys.has(attachment.chip.id)),
      );
    },
    [setAttachments],
  );

  useEffect(() => {
    onFilesChanged(attachments.map((attachment) => attachment.file));
  }, [attachments]);

  return (
    <StyledBox>
      {isFileUploadEnabled && attachments.length > 0 && (
        <StyledChipGroup
          items={attachments.map((a) => a.chip)}
          onRemove={onChipRemove}
          chipClassName={chipClassName}
          shape="box"
          size="small"
          variant="contained"
        />
      )}
      <Form onSubmit={handleOnSend} ref={formRef}>
        {isFileUploadEnabled && (
          <Tooltip
            title="Attach a dataset (Currently CSV only)"
            placement="top"
          >
            <span>
              <FileInputButton
                iconOnly={true}
                icon={<AttachFileIcon />}
                acceptedFileTypes={['text/csv']}
                onSelect={onFileSelect}
              />
            </span>
          </Tooltip>
        )}
        <FormControl sx={{ width: '100%' }}>
          <Textarea
            ref={textAreaRef}
            placeholder={placeholder}
            data-testid="message-bar-input"
            onKeyDown={handleEnterKeyPress}
            onChange={handleChange}
            value={text}
            {...mkTextBoxTrackAttrs({ message: text })}
          />
        </FormControl>

        <FormControl
          sx={{ alignSelf: 'flex-end' }}
          {...mkSendBtnTrackAttrs({ message: text })}
        >
          <SendButton
            type="submit"
            data-testid="message-bar-submit-button"
            disabled={isEmpty || isPending}
          >
            {isPending ? (
              <PendingIcon />
            ) : noraExperienceVariant === PAPER_FINDER ? (
              <StyledSearchIcon />
            ) : (
              <StyledSendIcon />
            )}
          </SendButton>
        </FormControl>
      </Form>
    </StyledBox>
  );
};

export default MessageBar;

const Textarea = styled(TextareaAutosize)`
  border: none;
  resize: none;
  background: none;

  &:focus {
    border: none;
  }

  // firefox
  &:focus-visible {
    outline: 0;
  }
`;

const chipClassName = 'chipClassName';
const StyledChipGroup = styled(ChipGroup)`
  gap: 0px;
  column-gap: 8px;
  .${chipClassName} {
    padding: 5px 8px 5px 6px;
    font-size: 14px;
    & button {
      padding-left: 2px;
    }
    & svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const StyledBox = styled(Box)`
  border-radius: ${({ theme }) => theme.spacing(0.5)};
  border: 1px solid rgba(250, 242, 233, 0.4);
  padding: ${({ theme }) => theme.spacing(1.25)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(1)};
  background-color: #032225;
  color: #ffffff;
`;

const Form = styled('form')`
  display: flex;
  align-items: center;
  width: 100%;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const SendButton = styled(IconButton)`
  &.MuiButtonBase-root {
    background-color: ${({ theme }) => `${theme.palette.secondary.main}`};
    border-radius: 2px;
    color: ${({ theme }) => `${theme.color['dark-teal-100'].hex}`};
    cursor: pointer;
    &:hover {
      color: ${({ theme }) => `${theme.color['dark-teal-80'].hex}`};
      background-color: ${({ theme }) => `${theme.palette.secondary.light}`};
    }

    &.MuiButtonBase-root.Mui-disabled {
      background-color: ${({ theme }) => `${theme.color['gray-40'].hex}`};
    }
  }
`;

// Fix after theme test bug has been fixed
// fill: ${({ theme }) => `${theme.palette.tertiary.main}`}
const StyledSendIcon = styled(SendIcon)`
  fill: rgba(15, 203, 140, 1);
`;

const StyledSearchIcon = styled(SearchIcon)`
  fill: rgba(15, 203, 140, 1);
`;
